<template>
  <v-dialog
    v-model="opened"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    width="600"
    @keydown.esc="$emit('update:opened', false)"
  >
    <v-card>
      <v-card-title class="title pb-0">
        Template Link
        <v-spacer />
        <v-btn
          small
          :fab="$vuetify.breakpoint.xsOnly"
          flat
          class="white error--text text-capitalize ma-0"
          @click="$emit('update:opened', false)"
        >
          <v-icon class="mr-1" size="18"> clear </v-icon>
          {{ $vuetify.breakpoint.smAndUp ? 'Close' : '' }}
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert
          :value="true"
          outline
          block
          :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
          color="info"
          class="text-xs-left"
        >
          Anyone can use the link below to sign up & get started with this
          template.
          <b
            >The copied link and QR code will only generate questionnaires of
            the selected year.</b
          >
          <br />
          <br />

          The Template Link is <b>not</b> intended for clients with multiple
          entities.
        </v-alert>

        <v-flex
          xs12
          class="text-xs-left"
          v-if="currentUser.firm_access === 'partner'"
        >
          <div class="mt-4">
            Assigned Firm User:
            <span class="error--text">*</span>
            <v-tooltip top>
              <v-icon size="18" slot="activator">info</v-icon>
              <span
                >New clients who sign up using the link below will be assigned
                to the selected firm user.</span
              >
            </v-tooltip>
          </div>

          <v-select
            v-model="selectedFirmUserId"
            placeholder="Select Firm User"
            class="default-input"
            :items="firmUsers"
            item-value="id"
            item-text="name"
            @change="saveQuestionnaireUserId"
            solo
            hide-details
          />
        </v-flex>

        <v-flex xs12 class="mt-4 text-xs-left" v-if="showGroupSelect">
          <div>
            Assigned Firm Group:
            <v-tooltip top>
              <v-icon size="18" slot="activator">info</v-icon>
              <span
                >New clients who sign up using the link below will be assigned
                to the selected firm group.</span
              >
            </v-tooltip>
          </div>

          <v-select
            v-model="selectedGroupId"
            placeholder="Select a group"
            :items="currentUserFirm.firm_groups"
            :disabled="currentUser.firm_access === 'group'"
            class="default-input"
            item-value="id"
            item-text="name"
            solo
            hide-details
          />
        </v-flex>

        <v-flex xs12 class="mt-4">
          <div class="text-xs-left">
            Tax Year:
            <span class="error--text">*</span>
          </div>

          <v-select
            class="default-input"
            label="Tax Year"
            v-model="selectedYear"
            :rules="fieldRules"
            :items="yearOptions"
            required
            solo
            hide-details
          />
        </v-flex>

        <v-flex xs12 class="mt-4" v-if="selectedYear">
          <v-layout> Template Link: </v-layout>

          <v-layout align-center>
            <v-text-field
              v-model="templateLink"
              ref="templateLink"
              disabled
              class="default-input"
              solo
              hide-details
            />
            <v-btn
              class="text-capitalize primary"
              @click="copyTextToClipboard(templateLink)"
            >
              Copy to Clipboard
            </v-btn>
          </v-layout>
        </v-flex>

        <v-flex xs12 class="mt-4" v-if="selectedYear">
          <v-layout mt-4> QR Code: </v-layout>

          <v-layout align-left mt-2>
            <canvas ref="qrcode" class="qrcode" />
          </v-layout>

          <v-layout align-left mt-2>
            <v-btn
              class="ml-0 text-capitalize primary"
              @click="downloadQRCode()"
            >
              Download QR Code
            </v-btn>
          </v-layout>
        </v-flex>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/api';
import { mapGetters } from 'vuex';
import QRCode from 'qrcode';
import { yearOptions } from '@/constants/variables';
import UtilsMixins from '@/mixins/UtilsMixins';

export default {
  props: {
    opened: {
      type: Boolean,
    },

    selectedQuestionnaire: {
      type: Object,
    },
  },
  mixins: [UtilsMixins],

  data() {
    return {
      firmUsers: [],
      selectedFirmUserId: null,
      selectedYear: null,
      yearOptions,
      fieldRules: [v => !!v || 'This field is required'],
      templateLink: '',
      currentUserFirm: null,
      selectedGroupId: null,
    };
  },

  methods: {
    saveQuestionnaireUserId() {
      API.questionnaireTemplate.updateQuestionnaireTemplate({
        id: this.selectedQuestionnaire.id,
        user_id: this.selectedFirmUserId,
      });
    },

    async getAllFirmUsers() {
      const response = await API.firm.getAllUsers();
      if (response.data.error) {
      } else {
        this.firmUsers = response.data.users;
      }
    },

    generateQRCode() {
      QRCode.toCanvas(this.$refs.qrcode, this.templateLink, function(error) {
        if (error) console.error(error);
        console.log('success!');
      });
    },

    downloadQRCode() {
      const imageUri = this.$refs.qrcode.toDataURL('image/jpeg', 1.0);
      this.downloadURI(
        imageUri,
        this.selectedQuestionnaire.name + ' (QR Code).jpeg',
      );
    },

    downloadURI(uri, name) {
      const link = document.createElement('a');
      link.href = uri;
      link.download = name;
      link.click();
    },

    async getSignUpLink() {
      try {
        this.templateLink = '';

        const response = await API.questionnaireTemplate.getSignUpLink({
          id: this.selectedQuestionnaire.id,
          taxYear: this.selectedYear,
          firmGroupId: this.selectedGroupId,
        });

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem getting the link',
          });
        } else {
          this.templateLink = response.data;
          this.templateLinkDialogOpened = true;
        }
      } catch (e) {
        console.log(e);
      }
    },

    async getCurrentUserFirm() {
      const response = await API.firm.getCurrentUserFirm();
      this.currentUserFirm = response.data;
    },
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),

    showGroupSelect() {
      return this.currentUserFirm && this.currentUserFirm.has_firm_groups;
    },
  },

  watch: {
    opened: {
      async handler(value) {
        if (value) {
          await this.getAllFirmUsers();
          this.selectedFirmUserId = this.selectedQuestionnaire.user_id;
          this.selectedYear = null;
          this.selectedGroupId = null;
        }
      },
    },

    selectedYear: {
      async handler(value) {
        if (value !== null) {
          await this.getSignUpLink();
          this.generateQRCode();
        }
      },
    },

    selectedGroupId: {
      async handler(value) {
        if (this.selectedYear !== null) {
          await this.getSignUpLink();
          this.generateQRCode();
        }
      },
    },
  },

  async created() {
    await this.getCurrentUserFirm();
  },
};
</script>

<style lang="postcss" scoped>
.qrcode {
  border: 1px solid #aaa;
  border-radius: 4px;
}
</style>
